import type { MockDatabase, MockRecord } from './database.ts';
import { ensureVqcaCategoryLevels } from './vqcaFixtures.ts';

const id = (group: number, index: number) => `${String(group).padStart(8, '0')}-0000-4000-8000-${String(index + 1).padStart(12, '0')}`;
const stamp = '2026-09-01T10:00:00.000Z';
export const ADMIN_DEMO_PSLS = ['Baroid', 'Cementing', 'Production Enhancement', 'Multi-Chem'] as const;
const migrationId = 'administration-demo-fixtures-1';
const roleScopeLabel = (psl: (typeof ADMIN_DEMO_PSLS)[number]) => psl === 'Production Enhancement' ? 'PE' : psl;

function profile(email: string, firstName: string, lastName: string, pslIndex: number) {
  return { userName: email, email, networkId: email.split('@')[0], firstName, lastName,
    fullName: `${firstName} ${lastName}`, company: 'Synthetic Demo Company', phone: '555-0100',
    countryId: 1, stateId: 1, address: '100 Demo Avenue', city: 'Houston', zip: '77032',
    psl: ADMIN_DEMO_PSLS[pslIndex], pslName: ADMIN_DEMO_PSLS[pslIndex], pslId: id(3, pslIndex) };
}

export function administrationDemoUsers(): MockRecord[] {
  return [];
}

/** Independent synthetic directory: the new/pending identities are NOT provisioned users. */
export function administrationDirectory(): MockRecord[] {
  const directoryEntries = [
    ['noaccess@example.test', 'No Access', 'Test Profile', 0],
    ['new.user1@qcdoc.internal', 'New', 'User 1', 0],
    ['new.user2@qcdoc.internal', 'New', 'User 2', 1],
    ['new.user3@qcdoc.internal', 'New', 'User 3', 2],
    ['new.user4@qcdoc.internal', 'New', 'User 4', 3],
  ] as const;
  return directoryEntries.map(([email, firstName, lastName, index]) => ({
    oid: id(113, index),
    ...profile(email, firstName, lastName, index),
    portal: 'QCDOC',
    isApproved: false,
    isActive: false,
    isLockedOut: false,
  }));
}

/** Additive, one-time migration within the existing storage version. Never reset users or requests. */
export function ensureAdministrationFixtures(database: MockDatabase): boolean {
  const levels = ensureVqcaCategoryLevels(database);
  const approvedInternal = new Set(['admin@qcdoc.internal', 'supplier@qcdoc.internal', 'qalab@qcdoc.internal', 'packaging@qcdoc.internal', 'reports@qcdoc.internal']);
  const internalUsers = (database.users ?? []).filter((user) => user.portal === 'QCDOC');
  database.users = [...internalUsers.filter((user) => {
    const email = String(user.email ?? '').trim().toLowerCase();
    return approvedInternal.has(email) || Boolean(user.directoryIdentityId);
  }), ...(database.users ?? []).filter((user) => user.portal !== 'QCDOC')];
  const append = (table: string, rows: MockRecord[], identity?: string) => {
    const target = database[table] ??= [];
    for (const row of rows) {
      if (!target.some((existing) => existing.oid === row.oid || (identity && String(existing[identity]).toLowerCase() === String(row[identity]).toLowerCase()))) target.push(row);
    }
  };
  // Keep the placeholder directory entries available for testing even after the migration flag has already been set.
  const placeholderDirectory = administrationDirectory().map((entry) => ({ ...entry, pslId: database.psls.find((psl) => psl.name === entry.psl)?.oid }));
  append('directory', placeholderDirectory, 'email');
  if (database.adminMigrations?.some((row) => row.oid === migrationId)) {
    return levels;
  }
  append('psls', ADMIN_DEMO_PSLS.map((name, index) => ({ oid: id(3, index), name, enabled: true, isActive: true })), 'name');
  append('roles', ['Administrator', ...ADMIN_DEMO_PSLS.flatMap((psl) => ['Administrator', 'ReadOnly', 'GQL'].map((role) => `${role} - ${roleScopeLabel(psl)}`))].map((roleName, index) => ({
    oid: id(111, index), roleId: id(111, index), roleName, enabled: true, portal: 'QCDOC',
    pslId: index ? database.psls.find((psl) => psl.name === ADMIN_DEMO_PSLS[Math.floor((index - 1) / 3)])?.oid : null,
  })), 'roleName');
  const demoUsers = administrationDemoUsers().map<MockRecord>((user) => ({ ...user, pslId: database.psls.find((psl) => psl.name === user.psl)?.oid }));
  // Avoid even email collisions with locally created accounts.
  append('users', demoUsers.filter((user) => !database.users?.some((existing) => String(existing.email).toLowerCase() === String(user.email).toLowerCase())), 'userName');
  // One-time directory snapshots for registered internal accounts, with no credentials or roles.
  append('directory', (database.users ?? []).filter((user) => user.portal === 'QCDOC').map((user) => ({
    ...profile(String(user.email), String(user.firstName ?? ''), String(user.lastName ?? ''), 0),
    ...Object.fromEntries(['userName', 'email', 'firstName', 'mi', 'lastName', 'fullName', 'company', 'phone', 'fax', 'mobile', 'countryId', 'stateId', 'address', 'city', 'zip', 'psl', 'pslId', 'pslName'].filter((key) => user[key] !== undefined).map((key) => [key, user[key]])),
    oid: `directory:${user.oid}`, networkId: String(user.userName).split('@')[0],
  })), 'email');
  append('userRequests', ([
    ['pending.user1@qcdoc.internal', 'Pending', 'User 1', 0],
    ['pending.user2@qcdoc.internal', 'Pending', 'User 2', 1],
    ['pending.user3@qcdoc.internal', 'Pending', 'User 3', 2],
    ['pending.user4@qcdoc.internal', 'Pending', 'User 4', 3],
  ] as const).map(([email, firstName, lastName, index]) => ({
    oid: id(112, index), ...profile(email, firstName, lastName, index),
    pslId: database.psls.find((psl) => psl.name === ADMIN_DEMO_PSLS[index])?.oid,
    portal: 'QCDOC', status: 16, statusId: 16, statusName: 'Created', reqStatusName: 'Created',
    createdDate: stamp, createdBy: email, notes: 'Synthetic directory-verified request ready for approval.',
  })).filter((request) => !database.users.some((user) => String(user.email).toLowerCase() === String(request.email).toLowerCase())), 'userName');
  for (const request of database.userRequests) {
    const name = ({ 16: 'Created', 17: 'Approved', 18: 'Rejected' } as Record<number, string>)[Number(request.status)];
    if (name) Object.assign(request, { reqStatusName: name, statusName: name });
  }
  append('adminMigrations', [{ oid: migrationId, appliedAt: new Date().toISOString() }]);
  return true;
}